import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import {
	INSPIRATION_DEFAULT_SCENARIO,
	INSPIRATION_SCENARIO_RATES,
} from "web_myplann/Inspiration/constants";
import {
	RETIREMENT_AGE,
	RETIREMENT_AGE_MAX,
	RETIREMENT_AGE_MIN,
	RETIREMENT_FALLBACK_AGE,
	RETIREMENT_INCOME_MIN,
	RETIREMENT_READY_COVERAGE,
	RETIREMENT_STATE_PENSION_DEFAULT,
} from "web_myplann/Inspiration/Retirement/constants";
import type {
	RetirementBaseline,
	RetirementContext,
	RetirementInputs,
	RetirementLevel,
} from "web_myplann/Inspiration/Retirement/Types/RetirementTypes";
import { getRetirementAnchors } from "web_myplann/Inspiration/Retirement/Utils/getRetirementAnchors";
import { getRetirementBridgeYears } from "web_myplann/Inspiration/Retirement/Utils/getRetirementBridgeYears";
import { getRetirementDates } from "web_myplann/Inspiration/Retirement/Utils/getRetirementDates";
import { getRetirementDrops } from "web_myplann/Inspiration/Retirement/Utils/getRetirementDrops";
import { getRetirementDropTotal } from "web_myplann/Inspiration/Retirement/Utils/getRetirementDropTotal";
import { getRetirementGap } from "web_myplann/Inspiration/Retirement/Utils/getRetirementGap";
import { getRetirementPace } from "web_myplann/Inspiration/Retirement/Utils/getRetirementPace";
import { getRetirementRequiredContribution } from "web_myplann/Inspiration/Retirement/Utils/getRetirementRequiredContribution";
import { getRetirementScenarios } from "web_myplann/Inspiration/Retirement/Utils/getRetirementScenarios";
import { getRetirementTargetCapital } from "web_myplann/Inspiration/Retirement/Utils/getRetirementTargetCapital";
import { getMonthlyAmountSliderMax } from "web_myplann/Inspiration/Utils/getMonthlyAmountSliderMax";
import { MONTHS_IN_YEAR } from "web_myplann/Shared/constants";

export const computeRetirementContext = (
	inputs: RetirementInputs,
	baseline: RetirementBaseline = {},
): RetirementContext => {
	const age = baseline.age ?? RETIREMENT_FALLBACK_AGE;
	const retirementAgeMin = Math.max(RETIREMENT_AGE_MIN, age + 1);
	const retirementAgeMax = Math.max(retirementAgeMin, RETIREMENT_AGE_MAX);
	const retirementAge = Math.min(
		retirementAgeMax,
		Math.max(retirementAgeMin, inputs.retirementAge ?? RETIREMENT_AGE),
	);
	const yearsToRetirement = Math.max(1, retirementAge - age);
	const bridgeYears = getRetirementBridgeYears(retirementAge);
	const dates = getRetirementDates({
		age,
		retirementAge,
		birthDate: baseline.birthDate,
		today: baseline.today,
	});
	const months = yearsToRetirement * MONTHS_IN_YEAR;

	const level: RetirementLevel =
		baseline.monthlyExpenses === undefined ? "cold" : "full";
	const drops = getRetirementDrops(
		baseline.portfolioItems ?? [],
		inputs.extraDrops ?? [],
		dates.retirementYear,
		inputs.dropsOff ?? [],
	);
	const dropTotal = getRetirementDropTotal(drops);
	const retirementExpenses =
		baseline.monthlyExpenses === undefined
			? undefined
			: Math.max(0, baseline.monthlyExpenses - dropTotal);
	const anchorBase =
		retirementExpenses === undefined
			? undefined
			: Math.max(RETIREMENT_INCOME_MIN, retirementExpenses);
	const incomeMax = getMonthlyAmountSliderMax(anchorBase);
	const anchors = getRetirementAnchors(anchorBase, incomeMax);

	const desiredIncome =
		inputs.desiredIncome ??
		(anchors.find((anchor) => anchor.key === "mid") ?? anchors[0]).amount;
	const statePension = inputs.statePension ?? RETIREMENT_STATE_PENSION_DEFAULT;
	const gap = getRetirementGap(desiredIncome, statePension);
	const drawdownMode = inputs.drawdownMode ?? RentDrawdownMode.ToZero;
	const targetCapital = getRetirementTargetCapital({
		desiredIncome,
		gap,
		bridgeYears,
		mode: drawdownMode,
	});

	const requiredContribution = getRetirementRequiredContribution(
		targetCapital,
		INSPIRATION_SCENARIO_RATES[INSPIRATION_DEFAULT_SCENARIO],
		months,
	);
	const contributionMax = getMonthlyAmountSliderMax(requiredContribution);
	const suggestedContribution = Math.min(contributionMax, requiredContribution);
	const monthlyContribution = Math.min(
		inputs.monthlyContribution ?? suggestedContribution,
		contributionMax,
	);

	const scenarios = getRetirementScenarios(
		monthlyContribution,
		months,
		targetCapital,
	);
	const selectedScenario = inputs.scenario ?? INSPIRATION_DEFAULT_SCENARIO;
	const shownCoverage =
		scenarios.find((scenario) => scenario.key === selectedScenario)?.coverage ??
		0;
	const pace = getRetirementPace({
		targetCapital,
		scenario: selectedScenario,
		months,
		monthlyContribution,
	});

	return {
		level,
		age,
		retirementAge,
		retirementAgeMin,
		retirementAgeMax,
		retirementAgeEdited: inputs.retirementAge !== undefined,
		yearsToRetirement,
		...dates,
		bridgeYears,
		monthlyExpenses: baseline.monthlyExpenses,
		drops,
		hasDeductibleDrops: drops.some((drop) => drop.deductible),
		dropTotal,
		retirementExpenses,
		anchors,
		activeAnchorKey: anchors.find((anchor) => anchor.amount === desiredIncome)
			?.key,
		desiredIncome,
		incomeMax,
		statePension,
		statePensionEdited: inputs.statePension !== undefined,
		statePercent:
			desiredIncome > 0
				? Math.min(100, Math.round((statePension / desiredIncome) * 100))
				: 0,
		gap,
		drawdownMode,
		targetCapital,
		hasIncome: (baseline.income ?? 0) > 0,
		freeCashflow: baseline.freeCashflow ?? 0,
		requiredContribution,
		contributionMax,
		suggestedContribution,
		monthlyContribution,
		scenarios,
		selectedScenario,
		shownCoverage,
		...pace,
		planCoversGoal:
			level === "full" &&
			targetCapital > 0 &&
			shownCoverage >= RETIREMENT_READY_COVERAGE,
		isCoveredByState: level === "full" && targetCapital === 0,
	};
};
